<template>
  <el-drawer
    v-model="visible"
    direction="rtl"
    append-to-body
    size="420px"
    :with-header="false"
    :close-on-click-modal="false"
    :close-on-press-escape="false"
    :show-close="false"
    class="dispatch-drawer"
  >
    <div class="dispatch">
      <div class="dispatch__head">
        <span class="title">下发进度</span>
      </div>

      <!-- 环形进度 -->
      <div class="dispatch__ring">
        <el-progress
          type="circle"
          :percentage="percent"
          :width="130"
          :stroke-width="10"
          color="#3A68FF"
        >
          <template #default>
            <div class="ring-center">
              <div class="ring-num">{{ doneCount }} / {{ total }}</div>
              <div class="ring-label">{{ finished ? '已完成' : '下发中…' }}</div>
            </div>
          </template>
        </el-progress>
        <div class="dispatch__summary">
          <span class="ok">已成功 {{ successCount }} 台</span>
          <span class="fail">失败 {{ failCount + notPushedCount }} 台</span>
          <span v-if="pendingCount > 0" class="pending">正在处理 {{ pendingCount }} 台</span>
        </div>
      </div>

      <!-- 按电站分组的逐目标结果 -->
      <div class="dispatch__list">
        <div v-for="grp in groupedResults" :key="grp.stationId" class="grp">
          <div class="grp__head">
            <span class="grp__name">{{ grp.stationName }}</span>
            <span class="grp__count">{{ grp.success }}/{{ grp.items.length }}</span>
          </div>
          <div v-for="item in grp.items" :key="item.station_id + ':' + item.elink_id" class="row" :class="stateOf(item)">
            <el-icon class="row__icon" :class="{ 'is-spin': stateOf(item) === 'pending' && dispatching }">
              <component :is="iconOf(stateOf(item))" />
            </el-icon>
            <span class="row__name">{{ item.elink_name }}</span>
            <span class="row__state">{{ stateText(item) }}</span>
            <span class="row__time">{{ rowTime(item) }}</span>
          </div>
        </div>
      </div>

      <div class="dispatch__footer">
        <el-button :disabled="!(failCount + notPushedCount) || dispatching" @click="retryFailed">全部重试</el-button>
        <el-button type="primary" :disabled="dispatching || pendingCount > 0" @click="onClose">关闭</el-button>
      </div>
    </div>
  </el-drawer>
</template>

<script setup>
import { ref, computed, onUnmounted } from 'vue'
import { Close, CircleCheck, CircleClose, Loading, Clock } from '@element-plus/icons-vue'
import { dispatchTemplate, dispatchResult } from '../api.js'

const visible = ref(false)
const dispatching = ref(false)
const results = ref([]) // 后端 DispatchResultItem 列表
let curTemplateId = null
let pollTimer = null

/** 单目标唯一键（跨重试稳定） */
function keyOf(x) {
  return `${x.station_id}:${x.elink_id}`
}

/** 归一化状态：success / fail / notPushed / pending */
function stateOf(item) {
  const s = item.status
  const r = item.result
  if (r === '成功' || s === '1') return 'success'
  if (r === '未推送' || s === 'not_pushed') return 'notPushed'
  if (r === '失败' || s === '2' || s === 'fail' || item.dispatched === false) return 'fail'
  return 'pending' // 待确认 / pending / 0
}

const total = computed(() => results.value.length)
const successCount = computed(() => results.value.filter((r) => stateOf(r) === 'success').length)
const failCount = computed(() => results.value.filter((r) => stateOf(r) === 'fail').length)
const notPushedCount = computed(() => results.value.filter((r) => stateOf(r) === 'notPushed').length)
const pendingCount = computed(() => results.value.filter((r) => stateOf(r) === 'pending').length)
const doneCount = computed(() => successCount.value + failCount.value + notPushedCount.value)
const percent = computed(() => (total.value ? Math.round((doneCount.value / total.value) * 100) : 0))
const finished = computed(() => !dispatching.value && pendingCount.value === 0 && total.value > 0)

const groupedResults = computed(() => {
  const map = new Map()
  for (const r of results.value) {
    if (!map.has(r.station_id)) {
      map.set(r.station_id, { stationId: r.station_id, stationName: r.station_name || r.station_id, items: [], success: 0 })
    }
    const g = map.get(r.station_id)
    g.items.push(r)
    if (stateOf(r) === 'success') g.success++
  }
  return Array.from(map.values())
})

function iconOf(state) {
  if (state === 'success') return CircleCheck
  if (state === 'fail' || state === 'notPushed') return CircleClose
  if (state === 'pending') return Loading
  return Clock
}

function stateText(item) {
  if (item.result) return item.result
  const st = stateOf(item)
  if (st === 'success') return '成功'
  if (st === 'fail') return item.msg || '失败'
  if (st === 'notPushed') return '未推送'
  return item.status === 'pending' ? '下发中' : '待确认'
}

function rowTime(item) {
  const t = item.receive_timestamp || item.send_timestamp
  return t ? String(t).slice(11, 19) : ''
}

/** 目标转 Target 结构 */
function toTarget(t) {
  return {
    station_id: t.station_id,
    station_name: t.station_name,
    elink_id: t.elink_id,
    elink_name: t.elink_name,
    traceing_no: t.traceing_no,
  }
}

/** 用后端返回的结果按 key 合并回本地列表 */
function mergeResults(arr) {
  if (!Array.isArray(arr) || !arr.length) return
  const map = new Map(arr.map((a) => [keyOf(a), a]))
  results.value = results.value.map((r) => {
    const u = map.get(keyOf(r))
    return u ? { ...r, ...u } : r
  })
}

/**
 * 打开并开始下发（第一步）
 * @param {Array} targets 已勾选目标
 * @param {Object} template 选中模板
 */
async function start(targets, template) {
  curTemplateId = template.id
  visible.value = true
  dispatching.value = true
  clearPoll()
  results.value = targets.map((t) => ({
    station_id: t.station_id,
    station_name: t.station_name,
    elink_id: t.elink_id,
    elink_name: t.elink_name,
    status: 'pending',
  }))
  try {
    const res = await dispatchTemplate({
      template_id: template.id,
      targets: targets.map(toTarget),
    })
    if (res?.code === 200) {
      const data = res.data
      const items = Array.isArray(data) ? data : (data?.items || [])
      if (items.length) results.value = items
    } else {
      window.dispatchNotify({ type: 'error', message: res?.msg || '下发失败' })
    }
  } catch (e) {
    window.dispatchNotify({ type: 'error', message: e?.msg || e?.message || '下发失败' })
  } finally {
    dispatching.value = false
  }
  startPolling()
}

/** 轮询间隔与总窗口 */
const POLL_INTERVAL = 3000
const POLL_WINDOW = 60 * 1000
let pollDeadline = 0

/** 第二步：1min 窗口内每 3s 轮询一次回报结果，谁有终态展示谁，到时仍未回报的判失败 */
function startPolling() {
  clearPoll()
  if (pendingCount.value <= 0) {
    dispatching.value = false
    return
  }
  dispatching.value = true
  pollDeadline = Date.now() + POLL_WINDOW
  pollOnce()
}

function markPendingAsFailed() {
  results.value = results.value.map((r) =>
    stateOf(r) === 'pending' ? { ...r, status: '2', result: '超时失败' } : r
  )
}

async function pollOnce() {
  // 只查询仍待确认的目标
  const items = results.value.filter((r) => stateOf(r) === 'pending').map(toTarget)
  try {
    const res = await dispatchResult(items)
    if (res?.code === 200) {
      const data = res.data
      const arr = Array.isArray(data) ? data : (data?.items || [])
      mergeResults(arr)
    }
  } catch (e) {
    console.warn('查询下发回报失败', e)
  }
  // 全部拿到终态，结束
  if (pendingCount.value <= 0) {
    dispatching.value = false
    return
  }
  // 超过 1min 窗口，剩余待确认判失败
  if (Date.now() >= pollDeadline) {
    markPendingAsFailed()
    dispatching.value = false
    return
  }
  // 继续下一轮
  pollTimer = setTimeout(pollOnce, POLL_INTERVAL)
}

/** 重试失败/未推送目标（重新走第一步下发） */
async function retryFailed() {
  const failed = results.value.filter((r) => ['fail', 'notPushed'].includes(stateOf(r)))
  if (!failed.length) return
  dispatching.value = true
  clearPoll()
  // 先把这些目标标记为 pending
  const failedKeys = new Set(failed.map(keyOf))
  results.value = results.value.map((r) =>
    failedKeys.has(keyOf(r)) ? { ...r, status: 'pending', result: '', dispatched: undefined } : r
  )
  try {
    const res = await dispatchTemplate({
      template_id: curTemplateId,
      targets: failed.map(toTarget),
    })
    if (res?.code === 200) {
      const data = res.data
      const items = Array.isArray(data) ? data : (data?.items || [])
      mergeResults(items)
    } else {
      window.dispatchNotify({ type: 'error', message: res?.msg || '重试失败' })
    }
  } catch (e) {
    window.dispatchNotify({ type: 'error', message: e?.msg || e?.message || '重试失败' })
  } finally {
    dispatching.value = false
  }
  startPolling()
}

function clearPoll() {
  if (pollTimer) {
    clearTimeout(pollTimer)
    pollTimer = null
  }
}

function onClose() {
  clearPoll()
  visible.value = false
}

onUnmounted(clearPoll)

defineExpose({ start })
</script>

<style scoped lang="scss">
.dispatch {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 16px;
}

.dispatch__head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;

  .title {
    font-size: 16px;
    font-weight: 600;
    color: #2e2e2e;
  }
  .close {
    cursor: pointer;
    color: rgba(46, 46, 46, 0.5);
    font-size: 18px;
    &:hover { color: #3A68FF; }
  }
}

.dispatch__ring {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;

  .ring-center {
    text-align: center;
    .ring-num { font-size: 20px; font-weight: 700; color: #2e2e2e; }
    .ring-label { font-size: 12px; color: rgba(46, 46, 46, 0.5); margin-top: 2px; }
  }
}

.dispatch__summary {
  display: flex;
  gap: 14px;
  font-size: 12px;

  .ok { color: #00C853; }
  .fail { color: #FF4D4F; }
  .pending { color: rgba(46, 46, 46, 0.5); }
}

.dispatch__list {
  flex: 1;
  min-height: 0;
  overflow: auto;
  margin-top: 16px;

  .grp {
    margin-bottom: 14px;

    &__head {
      display: flex;
      justify-content: space-between;
      font-size: 13px;
      font-weight: 600;
      color: #2e2e2e;
      margin-bottom: 6px;
    }
    &__count { color: #3A68FF; font-weight: 700; }
  }

  .row {
    display: flex;
    align-items: center;
    gap: 8px;
    padding: 6px 4px;
    font-size: 12px;
    border-bottom: 1px dashed rgba(0, 0, 0, 0.05);

    &__icon { font-size: 14px; }
    &__name { flex: 1; color: #2e2e2e; }
    &__state { color: rgba(46, 46, 46, 0.7); }
    &__time { color: rgba(46, 46, 46, 0.4); min-width: 60px; text-align: right; }

    &.success .row__icon { color: #00C853; }
    &.success .row__state { color: #00C853; }
    &.fail .row__icon, &.fail .row__state { color: #FF4D4F; }
    &.notPushed .row__icon, &.notPushed .row__state { color: #F5A623; }
    &.pending .row__icon { color: #3A68FF; }

    &__icon.is-spin { animation: dispatch-spin 1s linear infinite; }
  }
}

@keyframes dispatch-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

.dispatch__footer {
  display: flex;
  gap: 12px;
  padding-top: 12px;
  border-top: 1px solid rgba(0, 0, 0, 0.06);

  .el-button {
    flex: 1;
    color: #fff;
  }
  // 全部重试（次要按钮）实心灰底白字
  .el-button:not(.el-button--primary) {
    background: #909399;
    border-color: #909399;
    &.is-disabled { opacity: 0.5; }
  }
}
</style>
